import { ArrowDownLeft, ArrowUpRight, Loader2, RefreshCw, Receipt } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useCurrency } from '@/hooks/useCurrency';
import { useWalletTransactions, type CustomerAccount, type WalletTransaction } from '@/hooks/useCustomerAccounts';
import { cn } from '@/lib/utils';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  account: CustomerAccount | null;
}

const transactionTypeConfig: Record<string, { label: string; color: string; icon: typeof ArrowUpRight }> = {
  top_up: { label: 'Top Up', color: 'bg-green-500/10 text-green-600', icon: ArrowDownLeft },
  payment: { label: 'Payment', color: 'bg-red-500/10 text-red-600', icon: ArrowUpRight },
  refund: { label: 'Refund', color: 'bg-blue-500/10 text-blue-600', icon: ArrowDownLeft },
  adjustment: { label: 'Adjustment', color: 'bg-yellow-500/10 text-yellow-600', icon: RefreshCw },
};

function TransactionRow({ transaction }: { transaction: WalletTransaction }) {
  const { formatCurrency } = useCurrency();
  const config = transactionTypeConfig[transaction.transaction_type] || transactionTypeConfig.adjustment;
  const Icon = config.icon;
  const isPositive = transaction.amount > 0;

  return (
    <div className="flex items-center gap-3 py-3 border-b last:border-0">
      <div className={cn('p-2 rounded-full', config.color)}>
        <Icon className="h-4 w-4" />
      </div>
      
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <Badge variant="outline" className={cn('text-xs', config.color)}>
            {config.label}
          </Badge>
          {transaction.order_id && (
            <span className="text-xs text-muted-foreground flex items-center gap-1">
              <Receipt className="h-3 w-3" />
              Order
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 mt-1">
          <span className="text-xs text-muted-foreground">
            {new Date(transaction.created_at).toLocaleString()}
          </span>
          {transaction.processed_by_name && (
            <span className="text-xs text-muted-foreground">
              by {transaction.processed_by_name}
            </span>
          )}
        </div>
        {transaction.notes && (
          <p className="text-xs text-muted-foreground mt-1 truncate">
            {transaction.notes}
          </p>
        )}
      </div>

      <div className="text-right">
        <p className={cn('font-semibold', isPositive ? 'text-green-600' : 'text-red-600')}>
          {isPositive ? '+' : ''}{formatCurrency(transaction.amount)}
        </p>
        <p className="text-xs text-muted-foreground">
          Bal: {formatCurrency(transaction.balance_after)}
        </p>
      </div>
    </div>
  );
}

export function WalletTransactionsDialog({ open, onOpenChange, account }: Props) {
  const { formatCurrency } = useCurrency();
  const { data: transactions, isLoading, refetch } = useWalletTransactions(account?.id || null);

  if (!account) return null;

  const customerName = `${account.first_name || ''} ${account.last_name || ''}`.trim() || account.username;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] max-h-[80vh]">
        <DialogHeader>
          <DialogTitle>Wallet Transactions</DialogTitle>
          <DialogDescription>
            Transaction history for {customerName}
          </DialogDescription>
        </DialogHeader>

        {/* Current Balance */}
        <div className="bg-muted/50 rounded-lg p-4 flex items-center justify-between">
          <div>
            <p className="text-sm text-muted-foreground">Current Balance</p>
            <p className="text-2xl font-bold">{formatCurrency(account.wallet_balance)}</p>
          </div>
          <Button variant="outline" size="sm" onClick={() => refetch()}>
            <RefreshCw className="h-4 w-4 mr-1" />
            Refresh
          </Button>
        </div>

        {/* Transactions List */}
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : transactions && transactions.length > 0 ? (
          <div className="h-[400px] overflow-y-auto pr-2">
            <div className="space-y-0">
              {transactions.map((txn) => (
                <TransactionRow key={txn.id} transaction={txn} />
              ))}
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <Receipt className="h-8 w-8 text-muted-foreground mb-2" />
            <p className="text-sm text-muted-foreground">No transactions yet</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

